import React from "react";
import { render } from "react-dom";
import axios from "axios";
import qs from "qs";

import css from "./styles/main.scss";

import Intro from "./pages/Intro";
import Privacy from "./pages/Privacy";
import Outdated from "./pages/Outdated";
import TermsOfUse from "./pages/TermsOfUse";

import App from "./components/App";
import Login from "./pages/Login";
import LoginBackupCode from "./pages/LoginBackupCode";
import LoginPasswordReset from "./pages/LoginPasswordReset";
import LoginPasswordLost from "./pages/LoginPasswordLost";
import MfaSetup from "./pages/MfaSetup";

import Events from "./pages/Events";
import EventsDetail from "./pages/EventsDetail";
import EventsMeasurements from "./pages/EventsMeasurements";

import { Router, Route, IndexRoute } from "react-router";
import { Provider } from "react-redux";
import store, { history } from "./store";
import { logout, setUserData } from "./actions/authentication";
import { userMayLogin } from "./utils/auth";
import { consumeSkipNextAuthRecheck, consumeSkipNextSessionRestore, setAuthFailureHandler, setupAuthSessionInterceptors } from "./utils/authSession";
import { clearMfaSetupContext, setMfaSetupContext } from "./utils/mfaSetupContext";
import { clearLoginMfaContext } from "./utils/loginMfaContext";

const LEGACY_AUTH_STORAGE_KEYS = ["loginToken", "userUUID", "userFirstName", "userEmail", "userName", "userRequirePasswordChange", "onboarding_state"];

function clearLegacyAuthStorage() {
  LEGACY_AUTH_STORAGE_KEYS.forEach((key) => {
    window.localStorage.removeItem(key);
  });
}

function syncLegacyUserStorage(user) {
  if (!user) {
    return;
  }

  window.localStorage.userUUID = user.uuid;
  window.localStorage.userFirstName = user.first_name || user.initials || "";
  window.localStorage.userEmail = user.email || "";
  window.localStorage.userName = user.full_name || "";
  window.localStorage.userRequirePasswordChange = JSON.stringify(Boolean(user.require_password_change));
  window.localStorage.onboarding_state = user.onboarding_state || "";
}

function isPublicRoute(pathname) {
  return (
    pathname === "/" ||
    pathname === "/login" ||
    pathname.startsWith("/login/mfa/") ||
    pathname === "/mfa/setup" ||
    pathname.startsWith("/mfa/setup/") ||
    pathname === "/password-lost" ||
    pathname.startsWith("/password-reset/") ||
    pathname === "/outdated" ||
    pathname === "/privacy" ||
    pathname === "/terms-of-use" ||
    pathname === "/intro"
  );
}

function handleRequiredMfaSetupRedirect(setupTicket, redirectTo, redirect) {
  if (!setupTicket) {
    return false;
  }

  setMfaSetupContext({
    required: true,
    prompt: false,
    ticket: setupTicket,
    redirectTo: redirectTo || "/events"
  });

  if (typeof redirect === "function") {
    redirect("/mfa/setup");
  } else {
    history.push("/mfa/setup");
  }

  return true;
}

async function fetchAuthenticatedUser() {
  const response = await axios.get("/v2/auth/me/", {
    validateStatus: (status) => status >= 200 && status < 300
  });

  const loginCheck = userMayLogin(response.data);
  const mayLogin = loginCheck[0];
  const errorCode = loginCheck[1];

  if (!mayLogin) {
    const error = new Error("User may not login");
    error.authErrorCode = errorCode;
    throw error;
  }

  return response.data;
}

function setAuthenticatedUser(user) {
  store.dispatch(setUserData(user));
  syncLegacyUserStorage(user);
}

let restoreSessionPromise = null;

async function restoreSession() {
  if (restoreSessionPromise) {
    return restoreSessionPromise;
  }

  restoreSessionPromise = (async () => {
    const user = await fetchAuthenticatedUser();
    setAuthenticatedUser(user);
    return user;
  })();

  try {
    return await restoreSessionPromise;
  } finally {
    restoreSessionPromise = null;
  }
}

function handleAuthFailure(error) {
  const response = error && error.response;
  const responseData = response && response.data;
  const mfaCode = responseData && responseData.code;
  const setupTicket = responseData && responseData.mfa_setup_ticket;
  const currentPath = window.location.pathname + window.location.search;

  if (mfaCode === "mfa_setup_required" && handleRequiredMfaSetupRedirect(setupTicket, currentPath)) {
    clearLegacyAuthStorage();
    clearLoginMfaContext();
    store.dispatch(logout());
    return;
  }

  clearMfaSetupContext();
  clearLoginMfaContext();
  clearLegacyAuthStorage();
  store.dispatch(logout());
  if (!isPublicRoute(window.location.pathname)) {
    window.location.assign("/login");
  }
}

function forceLogout() {
  clearMfaSetupContext();
  clearLoginMfaContext();
  clearLegacyAuthStorage();
  store.dispatch(logout());
}

function checkAuthentication(nextState, replace, next) {
  (async () => {
    try {
      const state = store.getState();
      const stateUser = state && state.authentication && state.authentication.user;

      if (!stateUser) {
        await restoreSession();
      } else {
        const skipAuthRecheck = consumeSkipNextAuthRecheck();
        if (!skipAuthRecheck) {
          const latestUser = await fetchAuthenticatedUser();
          setAuthenticatedUser(latestUser);
        }
      }

      const user = store.getState() && store.getState().authentication && store.getState().authentication.user;
      if (user && user.onboarding_state !== APP_CONFIG.onboarding_state) {
        replace("/intro");
      }
    } catch (error) {
      const response = error && error.response;
      const responseData = response && response.data;
      const mfaCode = responseData && responseData.code;
      const setupTicket = responseData && responseData.mfa_setup_ticket;
      const redirectTo =
        (nextState.location && nextState.location.pathname ? nextState.location.pathname : "") +
        (nextState.location && nextState.location.search ? nextState.location.search : "");

      if (mfaCode === "mfa_setup_required" && handleRequiredMfaSetupRedirect(setupTicket, redirectTo, replace)) {
        clearLegacyAuthStorage();
        clearLoginMfaContext();
        store.dispatch(logout());
        return;
      }

      clearMfaSetupContext();
      clearLoginMfaContext();
      clearLegacyAuthStorage();
      store.dispatch(logout((error && error.authErrorCode) || "server_error"));
      replace("/login");
    } finally {
      if (typeof next === "function") {
        next();
      }
    }
  })();
}

// Check the latest version
axios({
  method: "get",
  baseURL: "/",
  url: "/meta.json?" + Math.round(Math.random() * 10000)
}).then((response) => {
  if (response && response.data && response.data.version != JSON.parse(COMMIT_HASH)) {
    window.location.reload(true);
  }
});

axios.defaults.baseURL = APP_CONFIG.api_url;
axios.defaults.timeout = 30000;
axios.defaults.headers.Accept = "application/json";
axios.defaults.withCredentials = true;
axios.defaults.paramsSerializer = (params) => {
  return qs.stringify(params, { arrayFormat: "repeat" });
};

setupAuthSessionInterceptors();
setAuthFailureHandler(handleAuthFailure);

if (!consumeSkipNextSessionRestore()) {
  restoreSession()
    .then((user) => {
      if (user && user.onboarding_state !== APP_CONFIG.onboarding_state && !window.location.pathname.startsWith("/intro")) {
        history.push("/intro");
      }
    })
    .catch((error) => {
      const response = error && error.response;
      const responseData = response && response.data;
      const mfaCode = responseData && responseData.code;
      const setupTicket = responseData && responseData.mfa_setup_ticket;

      if (mfaCode === "mfa_setup_required" && handleRequiredMfaSetupRedirect(setupTicket, window.location.pathname + window.location.search)) {
        clearLegacyAuthStorage();
        clearLoginMfaContext();
        store.dispatch(logout());
        return;
      }

      clearMfaSetupContext();
      clearLoginMfaContext();
      clearLegacyAuthStorage();
      store.dispatch(logout((error && error.authErrorCode) || null));
      if (!isPublicRoute(window.location.pathname)) {
        history.push("/login");
      }
    });
}

const router = (
  <Provider store={store}>
    <Router history={history}>
      <Route path="/" component={App}>
        <IndexRoute component={Login}></IndexRoute>
        <Route path="login" component={Login} />
        <Route path="login/mfa/backup" component={LoginBackupCode} />
        <Route path="mfa/setup" component={MfaSetup} />
        <Route path="mfa/setup/start" component={MfaSetup} />

        <Route path="password-reset/:uidb/:passwordtoken" component={LoginPasswordReset} onEnter={forceLogout} />
        <Route path="password-lost" component={LoginPasswordLost} onEnter={forceLogout} />

        <Route path="/password-reset" component={Outdated}></Route>
        <Route path="/password-confirm" component={Outdated}></Route>

        <Route path="/outdated" component={Outdated}></Route>

        <Route path="/intro" component={Intro}></Route>
        <Route path="/privacy" component={Privacy}></Route>
        <Route path="/terms-of-use" component={TermsOfUse}></Route>

        <Route path="/events" component={Events} onEnter={checkAuthentication}></Route>
        <Route path="/events/:eventId" component={EventsDetail} onEnter={checkAuthentication}></Route>
        <Route path="/events/:eventId/measurements" component={EventsMeasurements} onEnter={checkAuthentication}></Route>
      </Route>
    </Router>
  </Provider>
);

render(router, document.getElementById("root"));



// WEBPACK FOOTER //
// ./index.js